import { ROLLOUT_STATE } from '@prerender/feature-flags';
import api from '../../api/apiSlice';
import { FeatureFlag, FeatureFlagState } from './FeatureFlag';

export type FeatureFlagsResponse = {
  readonly configured: boolean;
  readonly flags: FeatureFlag[];
};

type FeatureFlagUpdateRequest = {
  readonly name: string;
  readonly state: FeatureFlagState;
  readonly percent?: number;
};

type FeatureFlagUpdateResponse = {
  readonly name: string;
  readonly state: FeatureFlagState;
  readonly percent?: number;
};

type BulkGrantRequest = {
  readonly flag: string;
  readonly userIds: string;
};

export type BulkGrantPreviewEntry =
  | {
      readonly userId: number;
      readonly status: 'willBeGranted' | 'alreadyHolds' | 'noSuchAccount';
      readonly cancelled: boolean;
      readonly blocked: boolean;
    }
  | { readonly entry: string; readonly status: 'notAUserId' };

export type BulkGrantPreview = {
  readonly flag: string;
  readonly warning?: 'off' | 'on' | 'internal';
  readonly entries: readonly BulkGrantPreviewEntry[];
};

export type BulkGrantResult = {
  readonly granted: readonly number[];
  readonly alreadyHeld: readonly number[];
  readonly noSuchAccount: readonly number[];
};

export const featureFlagsApi = api.injectEndpoints({
  endpoints: (builder) => ({
    listFeatureFlags: builder.query<FeatureFlagsResponse, void>({
      providesTags: ['FeatureFlags'],
      query: () => '/feature-flags',
    }),
    updateFeatureFlag: builder.mutation<FeatureFlagUpdateResponse, FeatureFlagUpdateRequest>({
      query: ({ name, state, percent }) => ({
        url: `/feature-flags/${name}`,
        method: 'PUT',
        body: state === ROLLOUT_STATE ? { state, percent } : { state },
      }),
      invalidatesTags: ['FeatureFlags'],
    }),
    previewBulkGrant: builder.mutation<BulkGrantPreview, BulkGrantRequest>({
      query: ({ flag, userIds }) => ({
        url: `/feature-flags/${flag}/account-grants/preview`,
        method: 'POST',
        body: { userIds },
      }),
    }),
    bulkGrantFlag: builder.mutation<BulkGrantResult, BulkGrantRequest>({
      query: ({ flag, userIds }) => ({
        url: `/feature-flags/${flag}/account-grants`,
        method: 'POST',
        body: { userIds },
      }),
    }),
  }),
});

export const {
  useListFeatureFlagsQuery,
  useUpdateFeatureFlagMutation,
  usePreviewBulkGrantMutation,
  useBulkGrantFlagMutation,
} = featureFlagsApi;
